import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import i18n from 'i18n';
import { compact, get, find, findIndex, filter } from 'lodash';
import PLAYER_TYPES from 'constants/player-types';
import getLocalized from 'utils/get-localized';
import getImagePath from 'utils/get-image-path';
import { IMDbIcon } from 'components/ContentTitle';
import { INIT_UI_LANGUAGE } from 'constants/languages';
import getPCRating from 'utils/get-pc-rating';
import scaleSize from 'utils/scale-size';
import {
  StyledPlayerHeaderWrapper,
  StyledChannelItem,
  StyledChannelItemLogo,
  StyledChannelItemData,
  StyledTitle,
  StyledMetadata,
  StyledCatchUpLabel,
} from '../StyledComponents';
import HeaderNavigationTip from './HeaderNavigationTip';

const trimMetaItems = (items, length = 2) => {
  return items
    .slice(0, length)
    .map((item) => getLocalized(item, item.UA))
    .join(', ');
};

const PlayerHeader = memo(({ playerType, content, navigation, timeshiftBegin, idle, disableChannelChange }) => {
  const Component = PlayerHeader[playerType] || (() => null);

  return (
    <StyledPlayerHeaderWrapper idle={idle}>
      <Component
        timeshiftBegin={timeshiftBegin}
        content={content}
        navigation={navigation}
        disableChannelChange={disableChannelChange}
      />
    </StyledPlayerHeaderWrapper>
  );
});

PlayerHeader.propTypes = {
  playerType: PropTypes.oneOf(Object.values(PLAYER_TYPES)).isRequired,
  content: PropTypes.object.isRequired,
  navigation: PropTypes.object,
  idle: PropTypes.bool.isRequired,
  disableChannelChange: PropTypes.bool,
};

const Channel = ({ content, navigation }) => {
  const prevChannel = navigation?.prevChannel;
  const nextChannel = navigation?.nextChannel;
  const currentProgram = content.currentProgram;
  const nextProgram = useMemo(() => {
    const programs = filter(content.epg, { isHeader: false });
    const i = findIndex(programs, { id: currentProgram?.id });

    if (i !== -1) return programs[i + 1];
  }, [content.epg, currentProgram]);

  const programTitle = currentProgram && getLocalized(currentProgram.titles, currentProgram.title);
  const nextProgramTitle = nextProgram && getLocalized(nextProgram.titles, nextProgram.title);

  const programTime =
    currentProgram &&
    `${moment(currentProgram.startTime).format('HH:mm')} - ${moment(currentProgram.endTime).format('HH:mm')}`;

  return (
    <>
      <HeaderNavigationTip channel={prevChannel} direction="up" />
      <StyledChannelItem>
        <StyledChannelItemLogo logo={getImagePath(content.logoUrl, scaleSize(130), scaleSize(130))} />
        <StyledChannelItemData>
          <span>{programTitle}</span>
          <i>{programTime}</i>
          {nextProgramTitle && (
            <i className="white">
              {i18n.t('player.playingNext')} {nextProgramTitle}
            </i>
          )}
        </StyledChannelItemData>
      </StyledChannelItem>
      <HeaderNavigationTip channel={nextChannel} direction="down" />
    </>
  );
};

const Timeshift = ({ content, timeshiftBegin, navigation, disableChannelChange }) => {
  const prevChannel = navigation?.prevChannel;
  const nextChannel = navigation?.nextChannel;
  const currentProgram = find(content?.epg, { startTime: timeshiftBegin });
  const nextProgram = useMemo(() => {
    const programs = filter(content.epg, { isHeader: false });
    const i = findIndex(programs, { id: currentProgram?.id });

    if (i !== -1) return programs[i + 1];
  }, [content.epg, currentProgram]);
  const programTitle = currentProgram && getLocalized(currentProgram.titles, currentProgram.title);
  const nextProgramTitle = nextProgram && getLocalized(nextProgram.titles, nextProgram.title);

  const programTime =
    currentProgram &&
    `${moment(currentProgram.startTime).format('HH:mm')} - ${moment(currentProgram.endTime).format('HH:mm')}`;

  return (
    <>
      <HeaderNavigationTip channel={prevChannel} direction="up" disableChannelChange={disableChannelChange} />
      <StyledChannelItem>
        <StyledChannelItemLogo logo={getImagePath(content.logoUrl, scaleSize(130), scaleSize(130))} />
        <StyledChannelItemData>
          <span>{programTitle}</span>
          <i>{programTime}</i>
          {nextProgramTitle && (
            <i className="white">
              {i18n.t('player.playingNext')} {nextProgramTitle}
            </i>
          )}
        </StyledChannelItemData>
      </StyledChannelItem>
      <HeaderNavigationTip channel={nextChannel} direction="down" disableChannelChange={disableChannelChange} />
    </>
  );
};

const Vod = ({ content }) => {
  const { pgRating = {} } = content;

  const title = getLocalized(content.titles, get(content, 'titles.UA.title', content.title.title));
  const rating = get(getPCRating(pgRating.name || pgRating.UA), ['age']);
  const IMDbRating = get(content, ['imdbRating']);
  const genresToShow = trimMetaItems(get(content, ['genres']));
  const countriesToShow = trimMetaItems(get(content, ['countriesOfOrigin']));
  const metaDataInfoLine1 = compact([get(content, ['title', 'year']), genresToShow, countriesToShow]).join(', ');

  return (
    <>
      <StyledTitle>{title}</StyledTitle>
      <StyledMetadata>
        <div>{metaDataInfoLine1}</div>
        <div>
          {rating}
          {IMDbRating && (
            <>
              <IMDbIcon /> {IMDbRating}
            </>
          )}
        </div>
      </StyledMetadata>
    </>
  );
};

const Trailer = ({ content }) => {
  const { pgRating = {} } = content;
  const title = getLocalized(content.titles, get(content, 'titles.UA.title', content.title.title));
  const rating = get(getPCRating(pgRating.name || pgRating.UA), ['age']);
  const IMDbRating = get(content, ['imdbRating']);
  const genresToShow = trimMetaItems(get(content, ['genres']));
  const countriesToShow = trimMetaItems(get(content, ['countriesOfOrigin']));
  const metaDataInfoLine1 = compact([get(content, ['title', 'year']), genresToShow, countriesToShow]).join(', ');

  return (
    <>
      <StyledTitle>
        {title} ({i18n.t('detailsVOD.trailer')})
      </StyledTitle>
      <StyledMetadata>
        <div>{metaDataInfoLine1}</div>
        <div>
          {rating}
          {IMDbRating && (
            <>
              <IMDbIcon /> {IMDbRating}
            </>
          )}
        </div>
      </StyledMetadata>
    </>
  );
};

const Series = ({ content }) => {
  const { pgRating = {} } = content;
  const title = getLocalized(content.titles, get(content, 'titles.UA.title', content.title));
  const rating = get(getPCRating(pgRating.name || pgRating.UA), ['age']);
  const IMDbRating = get(content, ['imdbRating']);
  const genresToShow = trimMetaItems(get(content, ['genres']));
  const countriesToShow = trimMetaItems(get(content, ['countriesOfOrigin']));
  const metaDataInfoLine1 = compact([get(content, ['title', 'year']), genresToShow, countriesToShow]).join(', ');

  return (
    <>
      <StyledTitle>{title}</StyledTitle>
      <StyledMetadata>
        <div>{metaDataInfoLine1}</div>
        <div>
          {rating}
          {IMDbRating && (
            <>
              <IMDbIcon /> {IMDbRating}
            </>
          )}
        </div>
      </StyledMetadata>
    </>
  );
};

const Catchup = ({ content }) => {
  const { channelMetadata, titles, title, startTime } = content;

  return (
    <StyledChannelItem>
      <StyledChannelItemLogo logo={getImagePath(channelMetadata.logoUrl, scaleSize(130), scaleSize(130))} />
      <StyledChannelItemData>
        <span>{getLocalized(titles, title)}</span>
        <i>
          <StyledCatchUpLabel>{i18n.t('epg.catchup')}</StyledCatchUpLabel>{' '}
          {moment(startTime).locale(INIT_UI_LANGUAGE).format('HH:mm, DD MMM')}
        </i>
      </StyledChannelItemData>
    </StyledChannelItem>
  );
};

const VodChannel = ({ content, navigation }) => {
  const prevChannel = navigation?.prevChannel;
  const nextChannel = navigation?.nextChannel;
  const nextProgram = navigation?.nextProgram;

  const { channelMetadata, titles, title, startTime, endTime, state } = content;
  const channelLogo = channelMetadata.logoUrl;
  const programTitle = getLocalized(titles, title);
  const programTime = `${moment(startTime).format('HH:mm')} - ${moment(endTime).format('HH:mm')}`;
  const nextProgramTitle = nextProgram && getLocalized(nextProgram.titles, nextProgram.title);

  if (state === 'LIVE_CATCHUP') {
    return (
      <>
        <HeaderNavigationTip channel={prevChannel} direction="up" />
        <StyledChannelItem>
          <StyledChannelItemLogo logo={getImagePath(channelLogo, scaleSize(130), scaleSize(130))} />
          <StyledChannelItemData>
            <span>{programTitle}</span>
            <i>{programTime}</i>
            {nextProgramTitle && (
              <i className="white">
                {i18n.t('player.playingNext')} {nextProgramTitle}
              </i>
            )}
          </StyledChannelItemData>
        </StyledChannelItem>
        <HeaderNavigationTip channel={nextChannel} direction="down" />
      </>
    );
  }

  return (
    <StyledChannelItem>
      <StyledChannelItemLogo logo={getImagePath(channelLogo, scaleSize(130), scaleSize(130))} />
      <StyledChannelItemData>
        <span>{getLocalized(titles, title)}</span>
        <i>
          <StyledCatchUpLabel>{i18n.t('epg.catchup')}</StyledCatchUpLabel>{' '}
          <span>{`${moment(startTime).locale(INIT_UI_LANGUAGE).format('HH:mm, DD MMM')}`}</span>
        </i>
        {nextProgramTitle && (
          <i className="white">
            {i18n.t('player.playingNext')} {nextProgramTitle}
          </i>
        )}
      </StyledChannelItemData>
    </StyledChannelItem>
  );
};

PlayerHeader[PLAYER_TYPES.CHANNEL] = Channel;
PlayerHeader[PLAYER_TYPES.TIMESHIFT] = Timeshift;
PlayerHeader[PLAYER_TYPES.VOD] = Vod;
PlayerHeader[PLAYER_TYPES.TRAILER] = Trailer;
PlayerHeader[PLAYER_TYPES.SERIES] = Series;
PlayerHeader[PLAYER_TYPES.CATCHUP] = Catchup;
PlayerHeader[PLAYER_TYPES.VOD_CHANNEL] = VodChannel;

export default PlayerHeader;
